import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import CustomTable from "../../../../../components/custom-table/custom-table";
import ToggleSwitchConfirmationDialog from "../../../../../components/modals/ToggleSwitchConfirmationDialog/ToggleSwitchConfirmationDialog";
import { MEDICAL_PA_BATCH_SIZE } from "../../../../../constants/medicalPAbatch";
import { useTablesContext } from "../../../../../context/tablesContextProvider";
import { NarAgentGridData } from "../../../../../data-model/medicalPaOrdersModel";
import { ModalId } from "../../../../../enums/modalId";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import {
  generateNarAgentGridTableData,
  NarAgentGridTableHeaders,
} from "../utils/narAgentGridTable";

const NarAgentGridTable = ({
  narAgentGridTableRef,
  exportingInProgress,
  apiData,
  clientId,
}: {
  narAgentGridTableRef: React.RefObject<HTMLDivElement>;
  exportingInProgress: boolean;
  apiData: NarAgentGridData[];
  clientId: string;
}) => {
  const [tableData, setTableData] = useState<any>([]);
  const [toggleSwitchData, setToggleSwitchData] = useState<{
    id: string;
    required: boolean;
    value: boolean;
  } | null>(null);

  const { openedModalId } = useSelector((state: RootState) => state.modal);

  const dispatch = useDispatch<AppDispatch>();

  const { setTableDataForContext } = useTablesContext();

  const exportDataForTable = () => {
    dispatch(
      setOpenedModalId({
        id: ModalId.ANALYTICS_DATE_PICKER_MODAL,
      }),
    );
  };
  const docSize = MEDICAL_PA_BATCH_SIZE;

  useEffect(() => {
    setTableData(generateNarAgentGridTableData(apiData));
  }, []);

  const handleEndIndexOfTable = (endIndex: number) => {
    const batchNumber = Math.ceil(tableData.length / docSize);
    setTableDataForContext("narAgentGrid", {
      lastBatchFetched: batchNumber,
    });
  };

  const handleToggleChange = (data: {
    name: string;
    required: boolean;
    value: boolean;
  }) => {
    const mappedData = {
      id: data.name,
      required: data.required,
      value: data.value,
    };

    dispatch(
      setOpenedModalId({
        id: ModalId.TOGGLE_SWITCH_CONFIRMATION_DIALOG,
        metaData: {
          id: mappedData.id,
          switchState: data.value,
          rowData: mappedData,
        },
      }),
    );
    updateToggleState(mappedData);
    setToggleSwitchData(mappedData);
  };

  const updateToggleState = (data: {
    id: string;
    required: boolean;
    value: boolean;
  }) => {
    setTableData((prevData: NarAgentGridData[]) =>
      prevData.map((row: any) =>
        row.agentMode?.id === data.id
          ? {
              ...row,
              agentMode: {
                ...row.agentMode,
                defaultChecked: data.value,
                rowData: {
                  ...row.agentMode.rowData,
                  agent: data.value,
                },
              },
            }
          : row,
      ),
    );
  };

  const handleModalCancel = () => {
    if (toggleSwitchData) {
      const revertedData = {
        ...toggleSwitchData,
        value: !toggleSwitchData.value,
      };
      updateToggleState(revertedData);
    }
    dispatch(closeModal());
    setToggleSwitchData(null);
  };

  const handleModalConfirm = () => {
    dispatch(closeModal());
    setToggleSwitchData(null);
  };

  useEffect(() => {
    if (openedModalId === ModalId.TOGGLE_SWITCH_CONFIRMATION_DIALOG) {
      openModal(ModalId.TOGGLE_SWITCH_CONFIRMATION_DIALOG);
    }
  }, [openedModalId]);

  return (
    <>
      <div ref={narAgentGridTableRef} className="flex h-full flex-col">
        <CustomTable
          tableHeaders={NarAgentGridTableHeaders}
          tableData={tableData}
          tableName={"narAgentGrid"}
          itemsPerPage={10}
          pagesPerView={4}
          count={tableData.length}
          totalCount={tableData.length}
          endIndexOfTable={handleEndIndexOfTable}
          searchingText={() => {}}
          hideSearchBar={true}
          isFetching={false || (false && tableData?.length === 0)}
          showExportButton={false}
          onExportButtonClick={exportDataForTable}
          showInLineLoader={false}
          showRefreshButton={false}
          hidePagination={true}
          onRefreshButton={() => {}}
          exportingInProgress={exportingInProgress}
          onChangeEmit={handleToggleChange}
        />
      </div>
      {openedModalId === ModalId.TOGGLE_SWITCH_CONFIRMATION_DIALOG && (
        <ToggleSwitchConfirmationDialog
          onClose={handleModalCancel}
          onConfirm={handleModalConfirm}
          clientId={clientId}
          metaData={
            toggleSwitchData
              ? {
                  id: toggleSwitchData.id,
                  switchState: toggleSwitchData.value,
                }
              : undefined
          }
        />
      )}
    </>
  );
};

export default NarAgentGridTable;
